import { useInstantSearch } from "react-instantsearch";
import * as algoliaInsightsService from "./../../Services/AlgoliaInsights.service";
import { GetPricesOfCurrency } from "./components/Hit";
import { useLayoutEffect } from "react";
import { createInsightsMiddleware } from "instantsearch.js/es/middlewares";

const GetViewedFilters = () => {
  const refinementLists = document.querySelectorAll(
    "[data-algolia-insights-filters] [data-refinement-list-insights]"
  );

  const values = Array.from(refinementLists)
    .map((refinementList) => {
      const key = refinementList.getAttribute("data-refinement-list");
      const inputValues = Array.from(
        refinementList.querySelectorAll(".algolia-refinement-list__checkbox")
      ).map((e) => (e as HTMLInputElement).value);

      return inputValues.map((e) => `${key}:${e}`);
    })
    .reduce((acc, curr) => [...acc, ...curr], []);

  return values;
};

interface InsightsMiddlewareProps {
  insightsCategory?: string;
}

export const InsightsMiddleware = ({
  insightsCategory = "",
}: InsightsMiddlewareProps) => {
  const { addMiddlewares } = useInstantSearch();

  useLayoutEffect(() => {
    const initialScrollY = window.scrollY;
    const addMiddleware = () => {
      const middleware = createInsightsMiddleware(
        Insights(initialScrollY, insightsCategory)
      );

      return addMiddlewares(middleware);
    };

    if (document.body.classList.contains("algolia-insights-loaded")) {
      addMiddleware();
    } else {
      document.body.addEventListener("algoliaInsightsLoaded", addMiddleware);
    }

    setTimeout(() => {
      HideSkeletonCards(initialScrollY);
    }, 2000);
  }, [addMiddlewares]);

  return null;
};

export const Insights = (initialScrollY, list = "") => {
  return {
    onEvent: (event) => {
      const { insightsMethod, widgetType, eventType, payload, hits } = event;
      const filteredHits = hits
        ? hits.filter((e) => e.__position !== undefined)
        : [];

      if (
        widgetType === "ais.hits" &&
        eventType === "view" &&
        filteredHits.length > 0
      ) {
        algoliaInsightsService.addToDataLayer({
          event: "algolia_hits_viewed",
          algoliaIndexName: payload.index,
          algoliaObjectIds: filteredHits.map((e) => e.objectID),
          algoliaQueryId: payload.queryID,
        });

        if (
          payload.index !== window.__litium.algoliaSettings.suggestionIndexName
        ) {
          algoliaInsightsService.addToDataLayerWithEcommerce({
            event: "productImpressions",
            ecommerce: {
              impressions: filteredHits.map((hit) => {
                const priceGroup = GetPricesOfCurrency(hit.price);
                return {
                  id: hit.objectID,
                  name: hit.name,
                  brand: hit.brand,
                  category: "", // Missing in index
                  variant: hit.variants[0].size,
                  price: priceGroup.actualPrice,
                  productDiscount: priceGroup.discount,
                  list: list,
                  position: hit.__position,
                };
              }),
            },
          });
        }
      }
      if (
        widgetType === "ais.hits" &&
        eventType === "click" &&
        filteredHits.length > 0
      ) {
        algoliaInsightsService.addToDataLayer({
          event: "algolia_hits_clicked",
          algoliaIndexName: payload.index,
          algoliaClickedObjectIds: filteredHits.map((e) => e.objectID),
          algoliaPositions: filteredHits.map((e) => e.__position),
          algoliaQueryId: payload.queryID,
        });
      }
      if (insightsMethod === "clickedFilters" && eventType === "click") {
        algoliaInsightsService.addToDataLayer({
          event: "algolia_clicked_filters",
          algoliaIndexName: payload.index,
          algoliaFilters: payload.filters,
          algoliaQueryId: payload.queryID,
        });
      }

      HideSkeletonCards(initialScrollY);
    },
  };
};

function HideSkeletonCards(initialScrollY) {
  const marginOfError = 100;
  const actualElementHeight =
    document.querySelector(".algolia-instant-search")?.clientHeight || 0;
  const newScrollPosition = window.scrollY - actualElementHeight;
  const elements = document.querySelectorAll(".algolia-skeleton-container");
  var elementsWereHidden = false;

  elements.forEach((e: HTMLElement) => {
    if (e.style.display !== "none") {
      e.style.display = "none";
      elementsWereHidden = true;
    }
  });

  if (
    elementsWereHidden &&
    newScrollPosition > marginOfError &&
    Math.abs(newScrollPosition - initialScrollY) <= marginOfError
  ) {
    window.scrollTo(0, initialScrollY);
  }
}
